import type { Game } from '@/types';

export interface GameInsight {
  label: string;
  tone: 'metric' | 'trust';
}

export interface RecapPresentation {
  label: string;
  detail: string;
  isOfficial: boolean;
  isSearchResult: boolean;
}

const OFFICIAL_RECAP_HOSTS = new Set([
  'nba.com',
  'www.nba.com',
  'nhl.com',
  'www.nhl.com',
]);

const YOUTUBE_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'm.youtube.com',
]);

function parseRecapUrl(recapUrl: string): URL | null {
  try {
    return new URL(recapUrl);
  } catch {
    return null;
  }
}

export function getRecapPresentation(recapUrl?: string | null): RecapPresentation | null {
  if (!recapUrl) {
    return null;
  }

  const parsedUrl = parseRecapUrl(recapUrl);
  if (!parsedUrl) {
    return {
      label: 'External link',
      detail: 'Unverified source, may reveal result',
      isOfficial: false,
      isSearchResult: false,
    };
  }

  const host = parsedUrl.hostname.toLowerCase();
  const path = parsedUrl.pathname.toLowerCase();
  const isOfficial = OFFICIAL_RECAP_HOSTS.has(host);
  const isSearchResult =
    YOUTUBE_HOSTS.has(host)
    && (path === '/results' || path.endsWith('/search'));

  if (isOfficial) {
    return {
      label: 'Official recap',
      detail: 'Official site, may reveal result',
      isOfficial: true,
      isSearchResult: false,
    };
  }

  if (isSearchResult) {
    return {
      label: 'YouTube search',
      detail: 'Unverified search, may reveal result',
      isOfficial: false,
      isSearchResult: true,
    };
  }

  if (YOUTUBE_HOSTS.has(host)) {
    return {
      label: 'YouTube link',
      detail: 'Unverified source, may reveal result',
      isOfficial: false,
      isSearchResult: false,
    };
  }

  return {
    label: 'External link',
    detail: 'Unverified source, may reveal result',
    isOfficial: false,
    isSearchResult: false,
  };
}

export function getGameInsights(game: Game, recapUrl?: string | null): GameInsight[] {
  const insights: GameInsight[] = [];
  const recapPresentation = getRecapPresentation(recapUrl);

  if (recapPresentation?.isOfficial) {
    insights.push({ label: recapPresentation.label, tone: 'trust' });
  }

  return insights.slice(0, 2);
}
